import type { TreeMapData } from '@revelio/replots';

import type {
  BreakdownData,
  NestedBreakdownSingleDataSet,
  NestedDataPoint,
  SingleDataPoint,
  TableDimension,
} from '../../DataProvider/types';
import type { TreemapTaxonomyCoverage } from '../../PageConfigProvider/types/item';

export type NestableTreeNode = {
  id: string | number;
  name: string;
  depth?: number;
  children?: readonly NestableTreeNode[];
};

// Taxonomy ids may be compound (`role_3_123`) while breakdown buckets carry the
// trailing numeric id, so both sides are keyed by that trailing segment.
export function nestedTreemapLookupKey(id: string | number): string {
  const raw = String(id);
  const tail = raw.includes('_') ? (raw.split('_').pop() ?? raw) : raw;
  return tail;
}

export type NestedTreemapParent = { parentId: string; parentName: string };

export function buildParentLookupFromTree(
  nodes: readonly NestableTreeNode[],
  childDepth: number
): Map<string, NestedTreemapParent> {
  const map = new Map<string, NestedTreemapParent>();

  const visit = (
    node: NestableTreeNode,
    parent: NestableTreeNode | undefined
  ): void => {
    const depth = node.depth ?? 0;
    if (depth === childDepth && parent !== undefined) {
      map.set(nestedTreemapLookupKey(node.id), {
        parentId: nestedTreemapLookupKey(parent.id),
        parentName: parent.name,
      });
    }
    if (node.children) {
      for (const child of node.children) {
        visit(child, node);
      }
    }
  };

  for (const root of nodes) {
    visit(root, undefined);
  }

  return map;
}

export const MAX_PARENT_GROUPS = 7;

export const taxonomyCoverageMaxGroups = (
  coverage: TreemapTaxonomyCoverage | undefined
): number | undefined => coverage?.topN?.groups;

const isOtherBucket = (datum: { id: string; name: string }): boolean =>
  datum.id === '-1' || datum.name.toLowerCase() === 'other';

export type FlatTreemapItem = {
  id: string;
  name: string;
  value: number;
  share?: number | null;
};

export type NestedTreemapParentTotal = {
  name: string;
  value: number;
  share: number | null;
};

function rankParentTotals(
  parentTotals: Map<string, NestedTreemapParentTotal>
): [string, NestedTreemapParentTotal][] {
  return [...parentTotals]
    .filter(
      ([parentId, total]) =>
        total.value > 0 &&
        total.name !== '' &&
        !isOtherBucket({ id: parentId, name: total.name })
    )
    .sort(([, a], [, b]) => b.value - a.value);
}

export function getRenderedParentIds(
  parentTotals: Map<string, NestedTreemapParentTotal>,
  maxGroups: number = MAX_PARENT_GROUPS
): string[] {
  return rankParentTotals(parentTotals)
    .slice(0, maxGroups)
    .map(([parentId]) => parentId);
}

export function buildNestedTreemapChildren(
  flatItems: readonly FlatTreemapItem[],
  parentLookup: Map<string, NestedTreemapParent>,
  parentTotals?: Map<string, NestedTreemapParentTotal>,
  options?: {
    omitStandaloneOther?: boolean;
    omitOther?: boolean;
    maxGroups?: number;
    parentIds?: ReadonlySet<string>;
  }
): TreeMapData['children'] {
  type Child = {
    name: string;
    value: number;
    id: string;
    share?: number | null;
  };
  type Group = {
    name: string;
    id: string;
    value: number;
    children: Child[];
  };

  const maxGroups = options?.maxGroups ?? MAX_PARENT_GROUPS;
  const omitOther = options?.omitOther === true;
  const omitStandaloneOther =
    omitOther || options?.omitStandaloneOther === true;

  const hasTotals = parentTotals !== undefined && parentTotals.size > 0;

  if (!hasTotals) {
    const groupMap = new Map<string, Group>();
    const groups: Group[] = [];
    const standalone: Child[] = [];
    let childRollup: Child | null = null;

    for (const item of flatItems) {
      const parent = parentLookup.get(nestedTreemapLookupKey(item.id));
      if (parent === undefined) {
        if (isOtherBucket(item)) {
          childRollup = {
            name: item.name,
            value: item.value,
            id: item.id,
            share: item.share,
          };
          continue;
        }
        standalone.push({
          name: item.name,
          value: item.value,
          id: item.id,
          share: item.share,
        });
        continue;
      }

      let group = groupMap.get(parent.parentId);
      if (group === undefined) {
        group = {
          name: parent.parentName,
          id: parent.parentId,
          value: 0,
          children: [],
        };
        groupMap.set(parent.parentId, group);
        groups.push(group);
      }
      group.children.push({
        name: item.name,
        value: item.value,
        id: item.id,
        share: item.share,
      });
      group.value += item.value;
    }

    if (childRollup !== null && !omitStandaloneOther) {
      standalone.push(childRollup);
    }

    return [...groups, ...standalone];
  }

  let leftoverValue = 0;
  let leftoverShare: number | null = 0;
  const addLeftover = (value: number, share: number | null | undefined) => {
    leftoverValue += value;
    leftoverShare =
      leftoverShare === null || typeof share !== 'number'
        ? null
        : leftoverShare + share;
  };

  const rankedParents = rankParentTotals(parentTotals);

  const groupMap = new Map<string, Group>();
  const groups: Group[] = [];
  for (const [parentId, total] of rankedParents) {
    if (
      groups.length >= maxGroups ||
      (options?.parentIds !== undefined && !options.parentIds.has(parentId))
    ) {
      addLeftover(total.value, total.share);
      continue;
    }
    const group: Group = {
      name: total.name,
      id: parentId,
      value: 0,
      children: [],
    };
    groupMap.set(parentId, group);
    groups.push(group);
  }

  for (const [parentId, total] of parentTotals) {
    if (isOtherBucket({ id: parentId, name: total.name }) && total.value > 0) {
      addLeftover(total.value, total.share);
    }
  }

  let childRollup: Child | null = null;
  for (const item of flatItems) {
    const parent = parentLookup.get(nestedTreemapLookupKey(item.id));
    if (parent === undefined) {
      if (isOtherBucket(item)) {
        childRollup = {
          name: item.name,
          value: item.value,
          id: item.id,
          share: item.share,
        };
      }
      continue;
    }
    const group = groupMap.get(parent.parentId);
    if (group === undefined) continue;
    group.children.push({
      name: item.name,
      value: item.value,
      id: item.id,
      share: item.share,
    });
    group.value += item.value;
  }

  for (const group of groups) {
    const trueTotal = parentTotals.get(group.id);
    if (
      !omitOther &&
      trueTotal !== undefined &&
      trueTotal.value > group.value
    ) {
      const childShareSum = group.children.reduce<number | null>(
        (sum, child) =>
          sum === null || typeof child.share !== 'number'
            ? null
            : sum + child.share,
        0
      );
      const otherShare =
        trueTotal.share !== null && childShareSum !== null
          ? Math.max(0, trueTotal.share - childShareSum)
          : undefined;
      group.children.push({
        name: 'Other',
        value: trueTotal.value - group.value,
        id: `other-${group.id}`,
        share: otherShare,
      });
      group.value = trueTotal.value;
    }
  }

  const standalone: Child[] = [];
  if (leftoverValue > 0 && !omitStandaloneOther) {
    standalone.push({
      name: childRollup?.name ?? 'Other',
      value: leftoverValue,
      id: childRollup?.id ?? '-1',
      share: leftoverShare ?? undefined,
    });
  }

  const universeValue =
    groups.reduce(
      (sum, group) => sum + (parentTotals.get(group.id)?.value ?? 0),
      0
    ) + leftoverValue;
  if (universeValue > 0) {
    const backfillMissingShares = (children: Child[]) => {
      for (const child of children) {
        if (typeof child.share !== 'number') {
          child.share = child.value / universeValue;
        }
      }
    };
    for (const group of groups) {
      backfillMissingShares(group.children);
    }
    backfillMissingShares(standalone);
  }

  return [...groups, ...standalone];
}

const DEFAULT_MAX_CELLS = 7;

function addParentTotal(
  totals: Map<string, NestedTreemapParentTotal>,
  key: string,
  { dim, value, share }: SingleDataPoint
): void {
  const existing = totals.get(key);
  const segmentShare = typeof share === 'number' ? share : null;
  if (existing === undefined) {
    totals.set(key, { name: dim, value: value ?? 0, share: segmentShare });
    return;
  }
  totals.set(key, {
    name: existing.name,
    value: existing.value + (value ?? 0),
    share:
      existing.share === null || segmentShare === null
        ? null
        : existing.share + segmentShare,
  });
}

export type LeveledTreemap = {
  children: TreeMapData['children'];
  parentLookup: Map<string, NestedTreemapParent>;
  parentLevel: TableDimension;
  childLevel: TableDimension;
};

export function buildTreemapFromLevels(
  { data, levels: [parentLevel, childLevel] }: NestedBreakdownSingleDataSet,
  {
    coverage,
    omitStandaloneOther,
  }: { coverage?: TreemapTaxonomyCoverage; omitStandaloneOther: boolean }
): LeveledTreemap {
  const maxGroups = taxonomyCoverageMaxGroups(coverage) ?? MAX_PARENT_GROUPS;
  const maxCells = coverage?.topN?.cells ?? DEFAULT_MAX_CELLS;
  const omitOther = coverage?.omitOther === true;

  const parentLookup = new Map<string, NestedTreemapParent>();
  const totals = new Map<string, NestedTreemapParentTotal>();
  const totalsByEntity = data.map((entity) => {
    const entityTotals = new Map<string, NestedTreemapParentTotal>();
    for (const segment of entity.breakdown) {
      const key = nestedTreemapLookupKey(segment.drilldownId ?? segment.dim);
      addParentTotal(totals, key, segment);
      addParentTotal(entityTotals, key, segment);
      for (const cell of segment.children) {
        parentLookup.set(nestedTreemapLookupKey(cell.drilldownId ?? cell.dim), {
          parentId: key,
          parentName: segment.dim,
        });
      }
    }
    return entityTotals;
  });

  const drawnParents = new Set(getRenderedParentIds(totals, maxGroups));
  const topCells = ({
    breakdown,
  }: BreakdownData<NestedDataPoint>): FlatTreemapItem[] => {
    const cells: FlatTreemapItem[] = [];
    for (const segment of breakdown) {
      const key = nestedTreemapLookupKey(segment.drilldownId ?? segment.dim);
      if (!drawnParents.has(key)) continue;
      for (const cell of segment.children) {
        cells.push({
          id: cell.drilldownId ?? cell.dim,
          name: cell.dim,
          value: cell.value ?? 0,
          share: cell.share,
        });
      }
    }
    return cells.sort((a, b) => b.value - a.value).slice(0, maxCells);
  };

  const [onlyEntity] = data;
  if (data.length === 1 && onlyEntity !== undefined) {
    return {
      children: buildNestedTreemapChildren(
        topCells(onlyEntity),
        parentLookup,
        totalsByEntity[0],
        { omitStandaloneOther, omitOther, maxGroups }
      ),
      parentLookup,
      parentLevel,
      childLevel,
    };
  }

  const children = data.map((entity, index) => {
    const cells = topCells(entity);
    const entityTotals = totalsByEntity[index];
    let drawnTotal = 0;
    for (const parentId of drawnParents) {
      drawnTotal += entityTotals?.get(parentId)?.value ?? 0;
    }
    const rest = drawnTotal - cells.reduce((sum, cell) => sum + cell.value, 0);
    return {
      name: entity.dim,
      value: drawnTotal,
      children: [
        ...cells.map((cell) => ({ ...cell, parentName: entity.dim })),
        ...(rest > 0 && !omitOther && !omitStandaloneOther
          ? [
              {
                name: 'Other',
                value: rest,
                id: `other-${entity.id ?? entity.dim}`,
                parentName: entity.dim,
              },
            ]
          : []),
      ],
    };
  });
  return { children, parentLookup, parentLevel, childLevel };
}
